// helpers/PricingFields/pricingFields.js

// Define filters as constants outside to prevent recreation
const FABRIC_FILTERS = { "properties.fabricType": ["MAIN", "SHEER"] };
const EMPTY_FILTERS = {};

// Curtain Fields (also used for Roman Blinds)
export const curtainPricingFields = [
    {
        section: "Raw Materials",
        sectionIcon: "bi bi-layers",
        fields: [
            {
                name: "baseFabric",
                label: "Base Fabric",
                type: "material-select",
                categorySlug: "fabric",
                required: true,
                filters: FABRIC_FILTERS,
                description: "Select the main fabric",
                displayFields: ["name", "code", "selling_price_per_unit", "unit"],
            },
            {
                name: "borderFabric",
                label: "Border Fabric",
                type: "material-select",
                categorySlug: "fabric",
                required: false,
                filters: FABRIC_FILTERS,
                description: "Select fabric for borders (optional)",
                displayFields: ["name", "code", "selling_price_per_unit", "unit"],
            }
        ]
    },
    {
        section: "Basic Configuration",
        sectionIcon: "bi bi-gear",
        fields: [
            {
                name: "borderType",
                label: "Border Type",
                type: "select",
                options: [
                    { value: "2-SIDE", label: "Two Sides" },
                    { value: "U-SHAPE", label: "U-Shape" },
                    { value: "HORIZONTAL", label: "Horizontal" }
                ],
                required: false,
                default: ""
            }
        ]
    },
    {
        section: "Value Additions",
        sectionIcon: "bi bi-palette",
        isRepeater: true,
        repeaterFields: [
            {
                name: "placement",
                label: "Placement",
                type: "select",
                options: [
                    { value: "BY-BORDER", label: "By Border" },
                    { value: "BY-PERCENTAGE", label: "By Percentage" },
                    { value: "BY-ALL_OVER", label: "All Over" },
                    { value: "BY-FIXED", label: "Fixed" }
                ],
                required: true,
                width: "col-md-3"
            },
            {
                name: "material",
                label: "Value Addition Type",
                type: "material-select",
                categorySlug: "value-addition",
                required: true,
                filters: EMPTY_FILTERS,
                displayFields: ["name", "unit", "selling_price_per_unit"],
                width: "col-md-3"
            },
            {
                name: "percentage",
                label: "Percentage",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                width: "col-md-2",
                description: "Percentate of application of item"
            },
            {
                name: "quantity",
                label: "Quantity",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                width: "col-md-2",
                description: "Stitches or hours per meter"
            },
            {
                name: "unit",
                label: "Unit",
                type: "text",
                readonly: true,
                width: "col-md-2",
                description: "Auto-filled from material"
            }
        ],
        target: "valueAdditions"
    },
    {
        section: "Accessories",
        sectionIcon: "bi bi-puzzle",
        isRepeater: true,
        repeaterFields: [
            {
                name: "placement",
                label: "Placement",
                type: "select",
                options: [
                    { value: "BY-BORDER", label: "By Border" },
                    { value: "BY-PERCENTAGE", label: "By Percentage" },
                    { value: "BY-ALL_OVER", label: "All Over" }
                ],
                required: true,
                width: "col-md-3"
            },
            {
                name: "unitPerMeter",
                label: "Units/Meter",
                type: "number",
                required: true,
                min: 0,
                step: 0.1,
                width: "col-md-3"
            },
            {
                name: "percentage",
                label: "Percentage",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                width: "col-md-2",
                description: "Percentate of application of item"
            },
            {
                name: "pricePerUnit",
                label: "Price Per Unit (₹)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                width: "col-md-3"
            },
            {
                name: "type",
                label: "Type (Optional)",
                type: "text",
                placeholder: "e.g., Zipper",
                width: "col-md-3"
            }
        ],
        target: "accessories"
    }
];

// Roller Blind Fields - Simple number inputs for each variant
export const rollerBlindPricingFields = [
    {
        section: "Vendor Product Pricing",
        sectionIcon: "bi bi-box",
        description: "Enter price per sq ft for each variant",
        fields: [
            {
                name: "CLASSIC_WITH_MANUAL",
                label: "Classic with Manual - Cost Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.CLASSIC_WITH_MANUAL.pricePerSqFt.cost_price"
            },
            {
                name: "CLASSIC_WITH_MANUAL",
                label: "Classic with Manual - Selling Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.CLASSIC_WITH_MANUAL.pricePerSqFt.selling_price"
            },
            {
                name: "CLASSIC_WITH_MOTORIZED ",
                label: "Classic with Motorized - Cost Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.CLASSIC_WITH_MOTORIZED.pricePerSqFt.cost_price"
            },
            {
                name: "CLASSIC_WITH_MOTORIZED",
                label: "Classic with Motorized - Selling Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.CLASSIC_WITH_MOTORIZED.pricePerSqFt.selling_price"
            },
            {
                name: "FACIA_WITH_MANUAL",
                label: "Facia with Manual - Cost Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.FACIA_WITH_MANUAL.pricePerSqFt.cost_price"
            },
            {
                name: "FACIA_WITH_MANUAL",
                label: "Facia with Manual - Selling Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.FACIA_WITH_MANUAL.pricePerSqFt.selling_price"
            },
            {
                name: "SIDE_CASSETTE_WITH_MOTORIZED",
                label: "Side Cassette with Motorized - Cost Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.SIDE_CASSETTE_WITH_MOTORIZED.pricePerSqFt.cost_price"
            },
            {
                name: "SIDE_CASSETTE_WITH_MOTORIZED",
                label: "Side Cassette with Motorized - Selling Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.SIDE_CASSETTE_WITH_MOTORIZED.pricePerSqFt.selling_price"
            },
            {
                name: "CORDLESS_WITH_FACIA_AND_MODULE",
                label: "Cordless with Facia & Module - Cost Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.CORDLESS_WITH_FACIA_AND_MODULE.pricePerSqFt.cost_price"
            },
            {
                name: "CORDLESS_WITH_FACIA_AND_MODULE",
                label: "Cordless with Facia & Module - Selling Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.CORDLESS_WITH_FACIA_AND_MODULE.pricePerSqFt.selling_price"
            }
        ]
    }
];

// Wooden Blind Fields - Simple number inputs for each variant
export const woodenBlindPricingFields = [
    {
        section: "Vendor Product Pricing",
        sectionIcon: "bi bi-box",
        description: "Enter price per sq ft for each variant",
        fields: [
            {
                name: "CORD_LOCK_WITH_WEB",
                label: "Cord Lock with Web - Cost Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.CORD_LOCK_WITH_WEB.pricePerSqFt.cost_price"
            },
            {
                name: "CORD_LOCK_WITH_WEB",
                label: "Cord Lock with Web - Selling Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.CORD_LOCK_WITH_WEB.pricePerSqFt.selling_price"
            },
            {
                name: "EASY_LIFT_WITH_WEB",
                label: "Easy Lift with Web - Cost Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.EASY_LIFT_WITH_WEB.pricePerSqFt.cost_price"
            },
            {
                name: "EASY_LIFT_WITH_WEB",
                label: "Easy Lift with Web - Selling Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.EASY_LIFT_WITH_WEB.pricePerSqFt.selling_price"
            },
            {
                name: "MOTORIZED_WITH_WEB",
                label: "Motorized with Web - Cost Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.MOTORIZED_WITH_WEB.pricePerSqFt.cost_price"
            },
            {
                name: "MOTORIZED_WITH_WEB",
                label: "Motorized with Web - Selling Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.MOTORIZED_WITH_WEB.pricePerSqFt.selling_price"
            },
            {
                name: "CORD_LOCK_WITH_LADDER_TAPE",
                label: "Cord Lock with Ladder Tape - Cost Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.CORD_LOCK_WITH_LADDER_TAPE.pricePerSqFt.cost_price"
            },
            {
                name: "CORD_LOCK_WITH_LADDER_TAPE",
                label: "Cord Lock with Ladder Tape - Selling Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.CORD_LOCK_WITH_LADDER_TAPE.pricePerSqFt.selling_price"
            },
            {
                name: "EASY_LIFT_WITH_LADDER_TAPE",
                label: "Easy Lift with Ladder Tape - Cost Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.EASY_LIFT_WITH_LADDER_TAPE.pricePerSqFt.cost_price"
            },
            {
                name: "EASY_LIFT_WITH_LADDER_TAPE",
                label: "Easy Lift with Ladder Tape - Selling Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.EASY_LIFT_WITH_LADDER_TAPE.pricePerSqFt.selling_price"
            },
            {
                name: "MOTORIZED_WITH_LADDER_TAPE",
                label: "Motorized with Ladder Tape - Cost Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.MOTORIZED_WITH_LADDER_TAPE.pricePerSqFt.cost_price"
            },
            {
                name: "MOTORIZED_WITH_LADDER_TAPE",
                label: "Motorized with Ladder Tape - Selling Price (₹/sq ft)",
                type: "number",
                required: true,
                min: 0,
                step: 1,
                target: "vendorPrices.MOTORIZED_WITH_LADDER_TAPE.pricePerSqFt.selling_price"
            }
        ]
    }
];

// Wooden Blind Fields
// export const woodenBlindPricingFields = [
//     {
//         section: "Vendor Product",
//         sectionIcon: "bi bi-box",
//         fields: [
//             {
//                 name: "operatingSystem",
//                 label: "Operating System",
//                 type: "select",
//                 required: true,
//                 options: [
//                     { value: "WEB", label: "Web" },
//                     { value: "LADDER_TAPE", label: "Ladder Tape" }
//                 ],
//                 target: "operatingSystem"
//             },
//             {
//                 name: "operationType",
//                 label: "Operation Type",
//                 type: "select",
//                 required: true,
//                 options: [
//                     { value: "CORD_LOCK", label: "Cord Lock" },
//                     { value: "EASY_LIFT", label: "Easy Lift" },
//                     { value: "MOTORIZED", label: "Motorized" }
//                 ],
//                 target: "operationType"
//             }
//         ]
//     },
//     {
//         section: "Hardware",
//         sectionIcon: "bi bi-gear",
//         fields: [
//             {
//                 name: "motor",
//                 label: "Motor Required",
//                 type: "boolean",
//                 default: false
//             },
//             {
//                 name: "remote",
//                 label: "Remote Required",
//                 type: "boolean",
//                 default: false
//             }
//         ]
//     }
// ];

// Cushion Fields
export const cushionPricingFields = [
    {
        section: "Raw Materials",
        sectionIcon: "bi bi-layers",
        fields: [
            {
                name: "baseFabric",
                label: "Fabric",
                type: "material-select",
                categorySlug: "fabric",
                required: true,
                filters: FABRIC_FILTERS,
                displayFields: ["name", "code", "selling_price_per_unit", "unit"],
                mappings: [
                    { target: "fabricPricePerInch", value: "selling_price_per_unit" }
                ]
            },
            {
                name: "filler",
                label: "Filler Material",
                type: "material-select",
                categorySlug: "filler",
                required: true,
                displayFields: ["name", "code", "selling_price_per_unit", "unit"],
                mappings: [
                    { target: "fillerPrice", value: "selling_price_per_unit" }
                ]
            }
        ]
    },
    {
        section: "Variants",
        sectionIcon: "bi bi-grid",
        fields: [
            {
                name: "variants",
                label: "Available Sizes",
                type: "variants-editor",
                required: true
            }
        ]
    }
];

// Quilt Fields
export const quiltPricingFields = [
    {
        section: "Raw Materials",
        sectionIcon: "bi bi-layers",
        fields: [
            {
                name: "baseFabric",
                label: "Fabric",
                type: "material-select",
                categorySlug: "fabric",
                required: true,
                filters: FABRIC_FILTERS,
                displayFields: ["name", "code", "selling_price_per_unit", "unit"],
                mappings: [
                    { target: "fabricPricePerInch", value: "selling_price_per_unit" }
                ]
            },
            {
                name: "borderFabric",
                label: "Border Fabric",
                type: "material-select",
                categorySlug: "fabric",
                required: false,
                filters: FABRIC_FILTERS,
                displayFields: ["name", "code", "selling_price_per_unit", "unit"],
                mappings: [
                    { target: "borderFabricPricePerInch", value: "selling_price_per_unit" }
                ]
            },
            {
                name: "filler",
                label: "Filler Material",
                type: "material-select",
                categorySlug: "filler",
                required: true,
                displayFields: ["name", "code", "selling_price_per_unit", "unit"],
                mappings: [
                    { target: "fillerPrice", value: "selling_price_per_unit" }
                ]
            }
        ]
    },
    {
        section: "Variants",
        sectionIcon: "bi bi-grid",
        fields: [
            {
                name: "variants",
                label: "Available Sizes",
                type: "variants-editor",
                required: true
            }
        ]
    }
];

// Upholstery Fields
export const upholsteryPricingFields = [
    {
        section: "Raw Materials",
        sectionIcon: "bi bi-layers",
        fields: [
            {
                name: "baseFabric",
                label: "Fabric",
                type: "material-select",
                categorySlug: "fabric",
                required: true,
                filters: FABRIC_FILTERS,
                displayFields: ["name", "code", "selling_price_per_unit", "unit"],
                mappings: [
                    { target: "fabricPricePerInch", value: "selling_price_per_unit" }
                ]
            }
        ]
    }
];

// Helper function to get fields by product type
export const getPricingFields = (productType) => {
    console.log("productType: ", productType);
    const fieldsMap = {
        CURTAIN: curtainPricingFields,
        ROMAN_BLIND: curtainPricingFields,
        ROLLER_BLIND: rollerBlindPricingFields,
        WOODEN_BLIND: woodenBlindPricingFields,
        CUSHION: cushionPricingFields,
        QUILT: quiltPricingFields,
        UPHOLSTERY: upholsteryPricingFields
    };

    return fieldsMap[productType] || curtainPricingFields;
};